import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  CircleGauge,
  CircleHelp,
  Flag,
  Gamepad2,
  Keyboard,
  Leaf,
  Lock,
  Maximize2,
  Pause,
  Play,
  RotateCcw,
  Snowflake,
  Sprout,
  Star,
  Timer,
  Tractor,
  Truck,
  Waves,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import packageJson from "../package.json";
import { GameScene } from "./game/GameScene";
import {
  calculateRating,
  finaleMission,
  getMission,
  getStageInfo,
  type MissionId,
  missionCatalogue,
  missions,
  type StageInfo,
} from "./game/missions";
import { playSuccess, playTone } from "./lib/audio";
import { useGameStore } from "./store/gameStore";

const appVersion =
  import.meta.env.VITE_APP_VERSION ?? `v${packageJson.version}-dev`;

const missionIcons = {
  mow: Leaf,
  snow: Snowflake,
  plant: Sprout,
  cargo: Truck,
  boat: Waves,
  finale: Flag,
} satisfies Record<MissionId, typeof Leaf>;

const isFinaleUnlocked = (
  results: ReturnType<typeof useGameStore.getState>["results"],
) => missions.every((mission) => Boolean(results[mission.id]));

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

export default function App() {
  const phase = useGameStore((state) => state.phase);
  const selected = useGameStore((state) => state.selected);
  const pause = useGameStore((state) => state.pause);
  const resume = useGameStore((state) => state.resume);
  const mission = getMission(selected);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (useGameStore.getState().phase === "playing") pause();
      else if (useGameStore.getState().phase === "paused") resume();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pause, resume]);

  useEffect(() => {
    document.documentElement.style.setProperty("--accent", mission.color);
    document.documentElement.style.setProperty("--accent-soft", mission.accent);
  }, [mission]);

  return (
    <main className={`app phase-${phase}`}>
      <GameScene />
      <div className="vignette" />
      {phase === "menu" && <MainMenu />}
      {phase === "briefing" && <Briefing />}
      {(phase === "playing" || phase === "paused") && <Hud />}
      {phase === "paused" && <PauseMenu />}
      {phase === "complete" && <CompleteScreen />}
      <div className="noise" />
    </main>
  );
}

function Brand() {
  return (
    <div className="brand">
      <span className="brand-mark">
        <span />
      </span>
      <span className="brand-name">
        BOGOSLOVKA<span>DYNAMICS</span>
      </span>
      <span className="brand-edition">VERSION / {appVersion}</span>
    </div>
  );
}

function MainMenu() {
  const selected = useGameStore((state) => state.selected);
  const selectMission = useGameStore((state) => state.selectMission);
  const showBriefing = useGameStore((state) => state.showBriefing);
  const results = useGameStore((state) => state.results);
  const mission = getMission(selected);
  const Icon = missionIcons[selected];
  const earnedStars = missions.reduce(
    (sum, item) => sum + (results[item.id]?.stars ?? 0),
    0,
  );
  const finaleUnlocked = isFinaleUnlocked(results);

  const choose = (id: MissionId) => {
    selectMission(id);
    playTone(420, 0.06, 0.025);
  };

  return (
    <section className="menu-shell">
      <header className="topbar">
        <Brand />
        <div className="top-stats">
          <span>
            <Star size={15} fill="currentColor" /> {earnedStars}/15
          </span>
          <span className="online-dot">СИСТЕМЫ В НОРМЕ</span>
        </div>
      </header>

      <div className="hero-copy">
        <div className="eyebrow">
          <span>ПОЛЕВОЙ КОНТРАКТ</span>
          <b>{mission.number} / 06</b>
        </div>
        <div className="mission-symbol">
          <Icon size={22} />
        </div>
        <h1>{mission.title}</h1>
        <p>{mission.description}</p>
        <div className="mission-meta">
          <span>
            <Tractor size={16} /> {mission.rover}
          </span>
          <span>
            <Timer size={16} /> НОРМАТИВ {formatTime(mission.parTime)}
          </span>
        </div>
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            showBriefing();
            playTone(600);
          }}
        >
          <span>ПЕРЕЙТИ К ЗАДАНИЮ</span>
          <ChevronRight size={19} />
        </button>
      </div>

      <div className="mission-rail-wrap">
        <div className="rail-label">
          <span>ВЫБЕРИТЕ МАШИНУ</span>
          <span>{finaleUnlocked ? "06 ДОСТУПНО" : "ФИНАЛ ЗАКРЫТ"}</span>
        </div>
        <nav className="mission-rail" aria-label="Список контрактов">
          {missionCatalogue.map((item) => {
            const ItemIcon = missionIcons[item.id];
            const result = results[item.id];
            const locked = item.id === "finale" && !finaleUnlocked;
            return (
              <button
                key={item.id}
                type="button"
                className={`mission-card ${selected === item.id ? "active" : ""} ${locked ? "locked" : ""}`}
                onClick={() => !locked && choose(item.id)}
                style={{ "--card-accent": item.color } as React.CSSProperties}
                aria-pressed={selected === item.id}
                disabled={locked}
              >
                <span className="card-number">{item.number}</span>
                {locked ? (
                  <Lock className="card-icon" size={20} />
                ) : (
                  <ItemIcon className="card-icon" size={21} />
                )}
                <span className="card-copy">
                  <b>{item.kicker}</b>
                  <small>
                    {locked ? "ЗАВЕРШИТЕ 5 КОНТРАКТОВ" : item.rover}
                  </small>
                </span>
                <span
                  className="card-stars"
                  title={`${result?.stars ?? 0} звёзд`}
                >
                  {[1, 2, 3].map((star) => (
                    <Star
                      key={star}
                      size={11}
                      fill={
                        star <= (result?.stars ?? 0) ? "currentColor" : "none"
                      }
                    />
                  ))}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="menu-hint">
        <Gamepad2 size={16} />
        <span>WASD / СТРЕЛКИ</span>
        <i /> <span>SPACE — ТОРМОЗ</span>
        <i /> <span>Q / E — КАМЕРА</span>
      </div>
    </section>
  );
}

function Briefing() {
  const selected = useGameStore((state) => state.selected);
  const startMission = useGameStore((state) => state.startMission);
  const quit = useGameStore((state) => state.quit);
  const mission = getMission(selected);
  const Icon = missionIcons[selected];

  return (
    <section className="modal-layer">
      <div className="briefing-panel">
        <button
          type="button"
          className="close-button"
          aria-label="Закрыть"
          onClick={quit}
        >
          <X size={20} />
        </button>
        <div className="briefing-index">КОНТРАКТ {mission.number}</div>
        <div className="briefing-icon">
          <Icon size={29} />
        </div>
        <div className="eyebrow">
          {mission.kicker.toUpperCase()} · {mission.rover}
        </div>
        <h2>{mission.title}</h2>
        <p>{mission.description}</p>
        <div className="objective-box">
          <span>ЦЕЛЬ ЗАДАНИЯ</span>
          <strong>{mission.objective}</strong>
        </div>
        <div className="briefing-grid">
          <div>
            <Timer size={18} />
            <span>
              Норматив<b>{formatTime(mission.parTime)}</b>
            </span>
          </div>
          <div>
            <CircleGauge size={18} />
            <span>
              Макс. скорость<b>{mission.maxSpeed.toFixed(1)} м/с</b>
            </span>
          </div>
          <div>
            <Star size={18} />
            <span>
              Оценка<b>по времени</b>
            </span>
          </div>
        </div>
        <button
          className="primary-button wide"
          type="button"
          onClick={() => {
            startMission();
            playTone(680, 0.1, 0.04);
          }}
        >
          <Play size={17} fill="currentColor" />
          <span>ЗАПУСТИТЬ РОВЕР</span>
        </button>
        <small className="briefing-note">
          Управление: WASD или стрелки · Space — тормоз · Q/E — камера · Esc —
          пауза
        </small>
      </div>
    </section>
  );
}

function Hud() {
  const [helpOpen, setHelpOpen] = useState(false);
  const selected = useGameStore((state) => state.selected);
  const completed = useGameStore((state) => state.completed);
  const total = useGameStore((state) => state.total);
  const elapsed = useGameStore((state) => state.elapsed);
  const speed = useGameStore((state) => state.speed);
  const event = useGameStore((state) => state.event);
  const pause = useGameStore((state) => state.pause);
  const mission = getMission(selected);
  const progress = Math.round((completed / Math.max(total, 1)) * 100);
  const stage = getStageInfo(selected, completed);
  const timeDelta = mission.parTime - elapsed;

  return (
    <section className="hud">
      <StageToast key={stage.key} info={stage} />
      {event && <DrivingEvent key={event.id} text={event.text} />}
      <header className="hud-top">
        <Brand />
        <div className="hud-mission">
          <span>{mission.kicker}</span>
          <b>{mission.title}</b>
        </div>
        <div className="hud-actions">
          <button
            type="button"
            className="hud-button help-button"
            aria-label="Показать управление"
            onClick={() => setHelpOpen(true)}
          >
            <CircleHelp size={19} />
          </button>
          <button
            type="button"
            className="hud-button"
            aria-label="Пауза"
            onClick={pause}
          >
            <Pause size={19} />
          </button>
        </div>
      </header>
      <aside className="progress-panel">
        <div className="progress-heading">
          <span>ВЫПОЛНЕНИЕ</span>
          <b>{progress}%</b>
        </div>
        <div className="progress-track">
          <span style={{ width: `${progress}%` }} />
        </div>
        <div className={`stage-summary ${stage.alert ? "alert" : ""}`}>
          <span>{stage.label}</span>
          <b>{stage.title}</b>
        </div>
        <p>{stage.objective}</p>
        <div className="progress-count">
          <strong>{String(completed).padStart(2, "0")}</strong>
          <span>
            / {String(total).padStart(2, "0")}
            <small>{mission.metric}</small>
          </span>
        </div>
      </aside>
      <div className={`telemetry ${speed > 0.5 ? "moving" : ""}`}>
        <div className="speed-readout">
          <CircleGauge size={17} />
          <span>
            <b>{Math.round(speed * 3.6)}</b> км/ч
          </span>
          <i>
            <span
              style={{
                width: `${Math.min(100, (speed / mission.maxSpeed) * 100)}%`,
              }}
            />
          </i>
        </div>
        <div>
          <Timer size={17} />
          <span>
            <b>{formatTime(elapsed)}</b> время
          </span>
        </div>
        <div className={timeDelta < 0 ? "warning" : ""}>
          <Flag size={17} />
          <span>
            <b>{formatTime(Math.abs(timeDelta))}</b>{" "}
            {timeDelta >= 0 ? "до норматива" : "сверх норматива"}
          </span>
        </div>
      </div>
      <div className="camera-hint">Q / E · ОБЗОР КАМЕРЫ</div>
      {helpOpen && <ControlsHelp onClose={() => setHelpOpen(false)} />}
      <MobileControls />
    </section>
  );
}

const controlRows = [
  { keys: "W / ↑", action: "Движение вперёд" },
  { keys: "S / ↓", action: "Задний ход" },
  { keys: "A D / ← →", action: "Поворот ровера" },
  { keys: "SPACE", action: "Тормоз" },
  { keys: "Q / E", action: "Обзор камеры" },
  { keys: "ESC", action: "Пауза" },
] as const;

function ControlsHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className="controls-help-backdrop" role="presentation">
      <section className="controls-help" aria-label="Управление ровером">
        <header>
          <span>
            <Keyboard size={17} /> УПРАВЛЕНИЕ
          </span>
          <button type="button" aria-label="Закрыть помощь" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="controls-list">
          {controlRows.map(({ keys, action }) => (
            <div key={keys}>
              <kbd>{keys}</kbd>
              <span>{action}</span>
            </div>
          ))}
        </div>
        <p>
          Ротор, косилка и посадочные диски включаются автоматически при
          движении вперёд.
        </p>
      </section>
    </div>
  );
}

function DrivingEvent({ text }: { text: string }) {
  return <div className="driving-event">{text}</div>;
}

function StageToast({ info }: { info: StageInfo }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setVisible(true);
    const timeout = window.setTimeout(() => setVisible(false), 2600);
    return () => window.clearTimeout(timeout);
  }, []);

  if (!visible) return null;
  return (
    <div className={`stage-toast ${info.alert ? "alert" : ""}`}>
      <span>{info.label}</span>
      <strong>{info.title}</strong>
      <small>{info.objective}</small>
    </div>
  );
}

function MobileControls() {
  const setVirtualInput = useGameStore((state) => state.setVirtualInput);
  const bind = (axis: "throttle" | "steer", value: number) => ({
    onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      const state = useGameStore.getState();
      setVirtualInput(
        axis === "throttle" ? value : state.virtualThrottle,
        axis === "steer" ? value : state.virtualSteer,
      );
    },
    onPointerUp: () => {
      const state = useGameStore.getState();
      setVirtualInput(
        axis === "throttle" ? 0 : state.virtualThrottle,
        axis === "steer" ? 0 : state.virtualSteer,
      );
    },
    onPointerCancel: () => setVirtualInput(0, 0),
  });
  return (
    <div className="mobile-controls">
      <div className="steer-controls">
        <button
          type="button"
          aria-label="Повернуть налево"
          {...bind("steer", 1)}
        >
          <ArrowLeft />
        </button>
        <button
          type="button"
          aria-label="Повернуть направо"
          {...bind("steer", -1)}
        >
          <ArrowRight />
        </button>
      </div>
      <div className="drive-controls">
        <button type="button" aria-label="Газ" {...bind("throttle", 1)}>
          <ChevronRight className="up-arrow" />
        </button>
        <button type="button" aria-label="Задний ход" {...bind("throttle", -1)}>
          <ChevronRight className="down-arrow" />
        </button>
      </div>
    </div>
  );
}

function PauseMenu() {
  const resume = useGameStore((state) => state.resume);
  const quit = useGameStore((state) => state.quit);
  return (
    <section className="modal-layer pause-layer">
      <div className="pause-panel">
        <span className="eyebrow">СИСТЕМА ПРИОСТАНОВЛЕНА</span>
        <h2>Пауза</h2>
        <button type="button" className="primary-button wide" onClick={resume}>
          <Play size={17} fill="currentColor" /> ПРОДОЛЖИТЬ
        </button>
        <button type="button" className="secondary-button" onClick={quit}>
          ВЫЙТИ В АНГАР
        </button>
      </div>
    </section>
  );
}

function CompleteScreen() {
  const selected = useGameStore((state) => state.selected);
  const elapsed = useGameStore((state) => state.elapsed);
  const score = useGameStore((state) => state.score);
  const stars = useGameStore((state) => state.stars);
  const results = useGameStore((state) => state.results);
  const quit = useGameStore((state) => state.quit);
  const startMission = useGameStore((state) => state.startMission);
  const mission = getMission(selected);
  const rating = calculateRating(elapsed, mission.parTime);
  const timeOverrun = Math.max(0, elapsed - mission.parTime);

  useEffect(() => playSuccess(), []);

  const next = useMemo(() => {
    if (selected === "finale") return missions[0];
    if (selected === "boat" && isFinaleUnlocked(results)) return finaleMission;
    return missions[
      (missions.findIndex((item) => item.id === selected) + 1) % missions.length
    ];
  }, [selected, results]);
  const nextMission = () => {
    useGameStore.getState().showBriefing(next.id);
    playTone(600);
  };

  return (
    <section className="modal-layer complete-layer">
      <div className="complete-panel">
        <div className="complete-kicker">КОНТРАКТ ВЫПОЛНЕН</div>
        <h2>{mission.title}</h2>
        <div className="result-stars">
          {[1, 2, 3].map((star) => (
            <Star
              key={star}
              size={42}
              fill={star <= stars ? "currentColor" : "transparent"}
              className={star <= stars ? "earned" : ""}
            />
          ))}
        </div>
        <div className="rating-reason">
          <span>ПОЧЕМУ {stars === 1 ? "1 ЗВЕЗДА" : `${stars} ЗВЕЗДЫ`}</span>
          <strong>{rating.reason}</strong>
        </div>
        <div className="score">
          {score.toLocaleString("ru-RU")}
          <span>ОЧКОВ</span>
        </div>
        <div className="result-grid">
          <div>
            <span>ВРЕМЯ</span>
            <b>{formatTime(elapsed)}</b>
            <small>
              {rating.time === 3
                ? `в нормативе ${formatTime(mission.parTime)}`
                : `медленнее на ${formatTime(timeOverrun)}`}
            </small>
          </div>
          <div>
            <span>НОРМАТИВ</span>
            <b>{formatTime(mission.parTime)}</b>
            <small>{rating.time === 3 ? "выполнен" : "нужно ускориться"}</small>
          </div>
        </div>
        <button
          className="primary-button wide"
          type="button"
          onClick={nextMission}
        >
          <span>
            {next.id === "finale" ? "ФИНАЛЬНЫЙ ПОЛИГОН" : "СЛЕДУЮЩИЙ КОНТРАКТ"}
          </span>
          <ChevronRight size={18} />
        </button>
        <div className="result-actions">
          <button type="button" onClick={startMission}>
            <RotateCcw size={15} /> ПОВТОРИТЬ
          </button>
          <button type="button" onClick={quit}>
            <Maximize2 size={15} /> В АНГАР
          </button>
        </div>
      </div>
    </section>
  );
}
